import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { getTrackersApi } from "../../../DAL/tracker/tracker";
import { getTrackerTagsApi } from "../../../DAL/trackerTag/trackerTag";
import {
  AUTOMATION_SCOPE_TYPES,
  createEmptyAutomation,
  deleteWorkflowFromAutomationViaApi,
  describeIndividualScope,
  getActionLabel,
  loadAutomationByIdFromApi,
  saveAutomationViaApi,
  saveWorkflowOnAutomationViaApi,
} from "../automations/automationStorage";
import TagChipPicker from "../TagChipPicker";
import IndividualAudiencePicker, {
  INITIAL_INDIVIDUAL_AUDIENCE,
  audienceFromScopeMember,
  buildIndividualScopeMember,
} from "./IndividualAudiencePicker";
import WorkflowBuilderPanel from "./WorkflowBuilderPanel";
import {
  ErrorText,
  INK,
  inputSx,
  MUTED,
  Panel,
  PRIMARY,
  PRIMARY_SOFT,
} from "./builderUi";

const BUILDER_PATH = "/tracker-automation-builder";
const LIST_PATH = "/tracker-automations";
const DEFAULT_AUTOMATION_NAME = createEmptyAutomation().name;

/** Same row as the old automation screen's workflow list. */
const WorkflowListItem = ({ item, active, onSelect, onRename, onDelete }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "stretch",
      borderBottom: "1px solid #f1f5f9",
      bgcolor: active ? PRIMARY_SOFT : "transparent",
      borderLeft: active ? `3px solid ${PRIMARY}` : "3px solid transparent",
      "&:hover": { bgcolor: active ? PRIMARY_SOFT : "#fff" },
    }}
  >
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-current={active ? "true" : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "left",
        border: 0,
        background: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        px: 1.75,
        py: 1.2,
      }}
    >
      <Box
        component="span"
        sx={{
          display: "block",
          fontWeight: 700,
          fontSize: 13.5,
          lineHeight: 1.35,
          color: active ? PRIMARY : INK,
          // Long names wrap instead of being cut off.
          overflowWrap: "anywhere",
          mb: 0.2,
        }}
      >
        {item.name || "Untitled"}
      </Box>
      <Box
        component="span"
        sx={{
          display: "block",
          fontSize: 11.5,
          lineHeight: 1.35,
          color: MUTED,
        }}
      >
        {item.status === "active" ? "Live" : "Draft"} ·{" "}
        {getActionLabel(item.action)}
      </Box>
    </Box>
    {onRename ? (
      <Tooltip title="Edit name">
        <IconButton
          size="small"
          aria-label={`Edit name of ${item.name || "workflow"}`}
          onClick={onRename}
          sx={{
            alignSelf: "center",
            color: MUTED,
            "&:hover": { color: PRIMARY },
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    ) : null}
    {onDelete ? (
      <IconButton
        size="small"
        aria-label={`Delete ${item.name || "workflow"}`}
        onClick={onDelete}
        sx={{
          alignSelf: "center",
          mr: 0.75,
          flexShrink: 0,
          color: MUTED,
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
      </IconButton>
    ) : null}
  </Box>
);

/**
 * Flow-style automation builder (new UI).
 * Same data, labels and API calls as automations/AddOrEditAutomationPage.
 * Create asks for name + tracker in a popup, then opens the builder;
 * the builder shows workflows on the left and the selected workflow as a
 * Trigger → Action flow in the middle.
 */
export default function AutomationBuilderPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id || id === "new";
  const trackerIdParam = searchParams.get("trackerId") || "";
  // Opened from a tracker's Automations tab (?from=tracker&trackerId=…):
  // back / Cancel return to that tab instead of the global list.
  const fromTracker =
    searchParams.get("from") === "tracker" && Boolean(trackerIdParam);
  const backPath = fromTracker
    ? `/trackers/${encodeURIComponent(trackerIdParam)}?tab=automations`
    : LIST_PATH;
  const backLabel = fromTracker ? "Journey automations" : "All automations";
  const builderQuery = fromTracker
    ? `?from=tracker&trackerId=${encodeURIComponent(trackerIdParam)}`
    : "";

  const [form, setForm] = useState(() =>
    createEmptyAutomation(
      trackerIdParam ? { id: trackerIdParam, name: "" } : null
    )
  );
  const [metaDraft, setMetaDraft] = useState({ name: "", enabled: true });
  const [editMetaOpen, setEditMetaOpen] = useState(false);
  // Workflow popup — "add" (name + trigger, before the editor opens) or
  // "rename" (name only).
  const [nameDialog, setNameDialog] = useState({
    open: false,
    mode: "add",
    workflow: null,
    name: "",
    error: "",
  });
  const [newWorkflowName, setNewWorkflowName] = useState("");
  // Bumped per "Add" so the editor starts a fresh draft each time.
  const [newDraftSeq, setNewDraftSeq] = useState(0);
  // Bumped by Cancel on a saved workflow to reload it as saved.
  const [resetSeq, setResetSeq] = useState(0);
  const [errors, setErrors] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteWorkflow, setDeleteWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [trackerOptions, setTrackerOptions] = useState([]);
  const [libraryTags, setLibraryTags] = useState([]);
  const [individualAudience, setIndividualAudience] = useState(
    INITIAL_INDIVIDUAL_AUDIENCE
  );

  const selectedWorkflowParam = searchParams.get("workflow") || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTrackersApi({ limit: 200 });
        if (cancelled) return;
        if (res?.code === 200 && Array.isArray(res.trackers)) {
          setTrackerOptions(
            res.trackers.map((t) => ({
              ...t,
              id: String(t._id || t.id),
            }))
          );
          return;
        }
        enqueueSnackbar(res?.message || "Could not load trackers", {
          variant: "error",
        });
      } catch (_e) {
        if (!cancelled) {
          enqueueSnackbar("Could not load trackers", { variant: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  // Shared tag library only — not every tag scraped off every journey
  // (that pulled in old defaults like "Goal Achieved" from many trackers).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTrackerTagsApi({ limit: 500, status: "active" });
        if (cancelled) return;
        const names = (Array.isArray(res?.tags) ? res.tags : [])
          .map((t) => String(t?.name || "").trim())
          .filter(Boolean);
        setLibraryTags(names);
      } catch (_e) {
        if (!cancelled) setLibraryTags([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Create: start from an empty automation (optionally for ?trackerId=).
  useEffect(() => {
    if (!isNew) return;
    // Tracker name is filled in once the API tracker list arrives (below).
    setForm(
      createEmptyAutomation(
        trackerIdParam ? { id: trackerIdParam, name: "" } : null
      )
    );
    setErrors({});
    setNotFound(false);
    setLoading(false);
  }, [isNew, trackerIdParam]);

  // Create: fill the tracker name once the tracker list arrives.
  useEffect(() => {
    if (!isNew || !form.trackerId || form.trackerName) return;
    const tracker = trackerOptions.find((t) => t.id === form.trackerId);
    if (!tracker?.name) return;
    setForm((prev) => ({
      ...prev,
      trackerName: tracker.name,
      name:
        !prev.name || prev.name === DEFAULT_AUTOMATION_NAME
          ? `${tracker.name} Automation`
          : prev.name,
    }));
  }, [isNew, form.trackerId, form.trackerName, trackerOptions]);

  // Edit: load the automation.
  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await loadAutomationByIdFromApi(id);
      if (cancelled) return;
      if (res?.automation) {
        setForm(res.automation);
        setIndividualAudience(
          audienceFromScopeMember(res.automation.scopeMember)
        );
        setMetaDraft({
          name: res.automation.name || "",
          enabled: res.automation.enabled !== false,
        });
        setNotFound(false);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  const workflows = useMemo(() => form.workflows || [], [form.workflows]);

  const selectedIsNew = selectedWorkflowParam === "new";
  const selectedWorkflowId =
    selectedIsNew || !selectedWorkflowParam ? null : selectedWorkflowParam;

  // Auto-select first workflow when none selected (edit only)
  useEffect(() => {
    if (isNew || !form?.id) return;
    if (selectedWorkflowParam) return;
    if (!workflows.length) return;
    const next = new URLSearchParams(searchParams);
    next.set("workflow", workflows[0].id);
    setSearchParams(next, { replace: true });
  }, [
    isNew,
    form?.id,
    workflows,
    selectedWorkflowParam,
    searchParams,
    setSearchParams,
  ]);

  const setSelectedWorkflow = (workflowKey) => {
    const next = new URLSearchParams(searchParams);
    if (!workflowKey) next.delete("workflow");
    else next.set("workflow", workflowKey);
    setSearchParams(next, { replace: true });
  };

  const allTagOptions = useMemo(() => {
    const names = [];
    const seen = new Set();
    const push = (tag) => {
      const value = String(tag || "").trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      names.push(value);
    };
    libraryTags.forEach(push);
    // Keep any already-selected scope tags that are legacy / not in library.
    (Array.isArray(form?.scopeTags) ? form.scopeTags : []).forEach(push);
    return names.sort((a, b) => a.localeCompare(b));
  }, [libraryTags, form?.scopeTags]);

  const syncIndividualScopeMember = (audience, trackerId = form.trackerId) => {
    const scopeMember = buildIndividualScopeMember(audience);
    const needsJourney = audience?.audienceType === "tracker";
    setForm((prev) => ({
      ...prev,
      scopeMember,
      trackerId: needsJourney ? trackerId || prev.trackerId || "" : "",
      trackerName: needsJourney
        ? trackerOptions.find((t) => t.id === (trackerId || prev.trackerId))
            ?.name || prev.trackerName
        : "",
    }));
  };

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (patch.trackerId && patch.trackerId !== prev.trackerId) {
        const tracker = trackerOptions.find((t) => t.id === patch.trackerId);
        next.trackerName = tracker?.name || "";
        if (!prev.name || prev.name === `${prev.trackerName} Automation`) {
          next.name = tracker?.name ? `${tracker.name} Automation` : prev.name;
        }
      }
      if (patch.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL) {
        const audience = { ...INITIAL_INDIVIDUAL_AUDIENCE };
        setIndividualAudience(audience);
        next.scopeMember = buildIndividualScopeMember(audience);
        next.trackerId = "";
        next.trackerName = "";
        if (
          !prev.name ||
          prev.name === DEFAULT_AUTOMATION_NAME ||
          prev.name === `${prev.trackerName} Automation` ||
          prev.name === "Tag automation"
        ) {
          next.name = "Individual member automation";
        }
      }
      if (
        patch.scopeType === AUTOMATION_SCOPE_TYPES.TAGS &&
        (!prev.name ||
          prev.name === DEFAULT_AUTOMATION_NAME ||
          prev.name === `${prev.trackerName} Automation` ||
          prev.name === "Individual member automation")
      ) {
        next.name = "Tag automation";
      }
      if (
        patch.scopeType === AUTOMATION_SCOPE_TYPES.TRACKER &&
        next.trackerId &&
        (!prev.name ||
          prev.name === "Tag automation" ||
          prev.name === "Individual member automation")
      ) {
        const tracker = trackerOptions.find((t) => t.id === next.trackerId);
        if (tracker?.name) next.name = `${tracker.name} Automation`;
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => {
        next[key] = "";
      });
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = "Name is required";
    if (form.scopeType === AUTOMATION_SCOPE_TYPES.TAGS) {
      if (!(form.scopeTags || []).length) {
        next.scopeTags = "Pick at least one tag";
      }
    } else if (form.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL) {
      const audience = individualAudience;
      const scopeMember = buildIndividualScopeMember(audience);
      if (!scopeMember) {
        next.scopeMember =
          audience.audienceType === "members"
            ? "Pick at least one member"
            : "Choose who this automation is for";
      }
      if (audience.audienceType === "tracker" && !form.trackerId) {
        next.trackerId = "Pick a journey";
      }
    } else if (!form.trackerId) {
      next.trackerId = "Pick a tracker";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    let saved = null;
    const scopeMember =
      form.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL
        ? buildIndividualScopeMember(individualAudience)
        : form.scopeMember;
    try {
      saved = await saveAutomationViaApi({
        ...form,
        scopeMember,
        name: form.name.trim(),
        description: (form.description || "").trim(),
        enabled: form.enabled !== false,
        status: form.enabled !== false ? "active" : "draft",
      });
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save automation", {
        variant: "error",
      });
    }
    setSaving(false);
    if (!saved?.id) return;
    enqueueSnackbar("Automation created", { variant: "success" });
    setLoading(true);
    navigate(`${BUILDER_PATH}/${saved.id}${builderQuery}`, { replace: true });
  };

  /** Saves name + enabled. Returns true on success. */
  const handleSaveMeta = async () => {
    const nextErrors = {};
    if (!String(metaDraft.name || "").trim()) {
      nextErrors.name = "Name is required";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return false;

    setSaving(true);
    try {
      const saved = await saveAutomationViaApi({
        ...form,
        name: String(metaDraft.name || "").trim(),
        description: (form.description || "").trim(),
        enabled: metaDraft.enabled !== false,
        status: metaDraft.enabled !== false ? "active" : "draft",
      });
      setForm(saved);
      setMetaDraft({
        name: saved.name || "",
        enabled: saved.enabled !== false,
      });
      enqueueSnackbar("Automation saved", { variant: "success" });
      return true;
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save automation", {
        variant: "error",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openEditMeta = () => {
    setMetaDraft((prev) => ({ ...prev, name: form.name || "" }));
    setErrors({});
    setEditMetaOpen(true);
  };

  const closeEditMeta = () => {
    if (saving) return;
    setMetaDraft((prev) => ({ ...prev, name: form.name || "" }));
    setErrors({});
    setEditMetaOpen(false);
  };

  const handleSaveEditMeta = async () => {
    if (await handleSaveMeta()) setEditMetaOpen(false);
  };

  const handleDeleteWorkflow = async () => {
    if (!deleteWorkflow?.id || !form?.id) return;
    try {
      const saved = await deleteWorkflowFromAutomationViaApi(
        form.id,
        deleteWorkflow.id
      );
      setForm(saved || form);
      setOpenDelete(false);
      const wasSelected = selectedWorkflowId === deleteWorkflow.id;
      setDeleteWorkflow(null);
      enqueueSnackbar("Workflow deleted", { variant: "success" });
      if (wasSelected) {
        const remaining = (saved?.workflows || []).filter(
          (w) => w.id !== deleteWorkflow.id
        );
        setSelectedWorkflow(remaining[0]?.id || null);
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not delete workflow", {
        variant: "error",
      });
    }
  };

  const handleAddWorkflow = () => {
    setNameDialog({
      open: true,
      mode: "add",
      workflow: null,
      name: "",
      error: "",
    });
  };

  const openRenameWorkflow = (workflow) => {
    setNameDialog({
      open: true,
      mode: "rename",
      workflow,
      name: workflow?.name || "",
      error: "",
    });
  };

  const closeNameDialog = () => {
    if (saving) return;
    const wasAdd = nameDialog.mode === "add";
    setNameDialog((prev) => ({ ...prev, open: false, error: "" }));
    // Opened on ?workflow=new without a name — go back to a saved workflow.
    if (wasAdd && selectedIsNew && !newWorkflowName) {
      setSelectedWorkflow(workflows[0]?.id || null);
    }
  };

  const handleSubmitNameDialog = async () => {
    const name = String(nameDialog.name || "").trim();
    if (!name) {
      setNameDialog((prev) => ({ ...prev, error: "Name is required" }));
      return;
    }
    if (nameDialog.mode === "add") {
      setNewWorkflowName(name);
      setNewDraftSeq((seq) => seq + 1);
      setNameDialog((prev) => ({ ...prev, open: false }));
      setSelectedWorkflow("new");
      return;
    }
    if (!form?.id || !nameDialog.workflow) return;
    setSaving(true);
    try {
      const saved = await saveWorkflowOnAutomationViaApi(form.id, {
        ...nameDialog.workflow,
        name,
      });
      setForm(saved);
      setNameDialog((prev) => ({ ...prev, open: false }));
      enqueueSnackbar("Workflow saved", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save workflow", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // ?workflow=new without a name (e.g. a refresh) — ask for it first.
  useEffect(() => {
    if (isNew || !form?.id || !selectedIsNew) return;
    if (newWorkflowName || nameDialog.open) return;
    setNameDialog({
      open: true,
      mode: "add",
      workflow: null,
      name: "",
      error: "",
    });
  }, [isNew, form?.id, selectedIsNew, newWorkflowName, nameDialog.open]);

  if (loading) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box sx={{ py: 4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
            Automation not found
          </Typography>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => navigate(backPath)}
          >
            Back to Automations
          </button>
        </Box>
      </div>
    );
  }

  const title = isNew ? "Create automation" : form.name || "Automation";
  const currentTracker =
    trackerOptions.find((t) => t.id === form.trackerId) ||
    (form.trackerId
      ? {
          id: form.trackerId,
          name: form.trackerName || "Journey",
        }
      : null);
  const showEditor = Boolean(form.id) && (selectedIsNew || selectedWorkflowId);
  const metaDialogOpen = isNew || editMetaOpen;

  const metaDialog = (
    <Dialog
      open={metaDialogOpen}
      onClose={isNew ? undefined : closeEditMeta}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>
        {isNew ? "Create automation" : "Edit automation"}
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <TextField
          id="automation-name"
          size="small"
          label="Name"
          required
          fullWidth
          autoFocus
          value={isNew ? form.name || "" : metaDraft.name}
          onChange={(e) => {
            const name = e.target.value;
            if (isNew) {
              update({ name });
            } else {
              setMetaDraft((prev) => ({ ...prev, name }));
              setErrors((prev) => ({ ...prev, name: "" }));
            }
          }}
          error={Boolean(errors.name)}
          helperText={errors.name || " "}
          sx={{ ...inputSx, mb: 1 }}
        />
        {isNew ? (
          <>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: MUTED,
                textTransform: "uppercase",
                letterSpacing: 0.35,
                mb: 0.5,
              }}
            >
              Apply to
            </Typography>
            <RadioGroup
              row
              value={form.scopeType || AUTOMATION_SCOPE_TYPES.TRACKER}
              onChange={(e) => update({ scopeType: e.target.value })}
              sx={{ mb: 1.5 }}
            >
              <FormControlLabel
                value={AUTOMATION_SCOPE_TYPES.TRACKER}
                control={<Radio size="small" />}
                label="A journey (tracker)"
              />
              <FormControlLabel
                value={AUTOMATION_SCOPE_TYPES.TAGS}
                control={<Radio size="small" />}
                label="People with tags"
              />
              <FormControlLabel
                value={AUTOMATION_SCOPE_TYPES.INDIVIDUAL}
                control={<Radio size="small" />}
                label="Individual member"
              />
            </RadioGroup>

            {(form.scopeType || AUTOMATION_SCOPE_TYPES.TRACKER) ===
            AUTOMATION_SCOPE_TYPES.TAGS ? (
              <Box sx={{ mb: 0.5 }}>
                <TagChipPicker
                  label="Tags"
                  options={allTagOptions}
                  value={form.scopeTags || []}
                  onChange={(scopeTags) => update({ scopeTags })}
                  emptyLabel="No tags yet — create some under Trackers → Tags."
                />
                {errors.scopeTags ? (
                  <ErrorText sx={{ mt: 0.75 }}>{errors.scopeTags}</ErrorText>
                ) : (
                  <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
                    Runs for anyone who has at least one of these library tags
                    (any journey).
                  </Typography>
                )}
              </Box>
            ) : form.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL ? (
              <IndividualAudiencePicker
                audience={individualAudience}
                onChange={(audience) => {
                  setIndividualAudience(audience);
                  syncIndividualScopeMember(audience, form.trackerId);
                  setErrors((prev) => ({
                    ...prev,
                    scopeMember: "",
                    trackerId: "",
                  }));
                }}
                trackerId={form.trackerId}
                trackerName={form.trackerName}
                trackerOptions={trackerOptions}
                onTrackerChange={(trackerId) => {
                  const tracker = trackerOptions.find((t) => t.id === trackerId);
                  setForm((prev) => ({
                    ...prev,
                    trackerId,
                    trackerName: tracker?.name || "",
                    scopeMember: buildIndividualScopeMember(individualAudience),
                  }));
                  setErrors((prev) => ({ ...prev, trackerId: "" }));
                }}
                errors={errors}
              />
            ) : (
              <TextField
                id="automation-tracker"
                select
                size="small"
                label="Journey"
                required
                fullWidth
                value={form.trackerId || ""}
                onChange={(e) => update({ trackerId: e.target.value })}
                error={Boolean(errors.trackerId)}
                helperText={errors.trackerId || " "}
                sx={inputSx}
              >
                <MenuItem value="">Select journey…</MenuItem>
                {form.trackerId &&
                !trackerOptions.some((t) => t.id === form.trackerId) ? (
                  <MenuItem value={form.trackerId}>
                    {form.trackerName || "—"}
                  </MenuItem>
                ) : null}
                {trackerOptions.map((tracker) => (
                  <MenuItem key={tracker.id} value={tracker.id}>
                    {tracker.name || "Untitled"}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        ) : (
          <TextField
            id="automation-scope"
            size="small"
            label="Applies to"
            fullWidth
            value={
              form.scopeType === AUTOMATION_SCOPE_TYPES.TAGS
                ? `Tags: ${(form.scopeTags || []).join(", ") || "—"}`
                : form.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL
                ? describeIndividualScope(form)
                : form.trackerName || "—"
            }
            disabled
            helperText=" "
            sx={inputSx}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={isNew ? () => navigate(backPath) : closeEditMeta}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="small-contained-button"
          onClick={isNew ? handleCreate : handleSaveEditMeta}
          disabled={saving}
        >
          {isNew
            ? saving
              ? "Creating…"
              : "Create automation"
            : saving
            ? "Saving…"
            : "Save"}
        </button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this workflow?"
        handleAgree={handleDeleteWorkflow}
        open={false}
        setOpen={() => {}}
      />
      {metaDialog}

      <Dialog
        open={nameDialog.open}
        onClose={closeNameDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>
          {nameDialog.mode === "add" ? "Add workflow" : "Edit workflow"}
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            id="workflow-name"
            size="small"
            label="Name"
            placeholder="Workflow name"
            required
            fullWidth
            autoFocus
            value={nameDialog.name}
            onChange={(e) => {
              const name = e.target.value;
              setNameDialog((prev) => ({ ...prev, name, error: "" }));
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              // Otherwise the Enter also clicks the "Add" button that gets
              // focus back when the popup closes, opening it again.
              e.preventDefault();
              handleSubmitNameDialog();
            }}
            error={Boolean(nameDialog.error)}
            helperText={nameDialog.error || " "}
            sx={inputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={closeNameDialog}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="small-contained-button"
            onClick={handleSubmitNameDialog}
            disabled={saving}
          >
            {nameDialog.mode === "add" ? "Add" : saving ? "Saving…" : "Save"}
          </button>
        </DialogActions>
      </Dialog>

      <div className="container-fluid tracker-module-page">
        {/* Header: breadcrumb (left), Enabled + Save (right) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: errors.name && !editMetaOpen ? 0.5 : 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={() => navigate(backPath)}
              sx={{ p: 0.75 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              component="button"
              type="button"
              onClick={() => navigate(backPath)}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                cursor: "pointer",
                color: MUTED,
                fontSize: 13,
                lineHeight: 1.2,
                "&:hover": { color: PRIMARY },
              }}
            >
              {backLabel}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 13, lineHeight: 1.2 }}>
              /
            </Typography>
            <Typography
              sx={{
                color: INK,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.2,
                maxWidth: 320,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            {isNew ? null : (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={openEditMeta}
                  sx={{ p: 0.5, color: MUTED }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {isNew ? null : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                ml: "auto",
              }}
            >
              <FormControlLabel
                sx={{ ml: 0, mr: 0 }}
                control={
                  <Switch
                    checked={metaDraft.enabled}
                    onChange={(e) => {
                      // Read before the state updater runs — React may
                      // restore the controlled checkbox by then.
                      const enabled = e.target.checked;
                      setMetaDraft((prev) => ({ ...prev, enabled }));
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Enabled
                  </Typography>
                }
              />

              <button
                type="button"
                className="small-contained-button"
                onClick={handleSaveMeta}
                disabled={saving}
              >
                {saving && !editMetaOpen ? "Saving…" : "Save"}
              </button>
            </Box>
          )}
        </Box>

        {isNew ? null : (
          <>
            {errors.name && !editMetaOpen ? (
              <ErrorText sx={{ mt: 0, mb: 2, ml: 0.5 }}>
                {errors.name}
              </ErrorText>
            ) : null}

            {/* Body: workflows | flow canvas */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "260px minmax(0, 1fr)",
                },
                gap: 2.5,
                alignItems: "start",
              }}
            >
              {/* Workflows list */}
              <Panel
                sx={{
                  overflow: "hidden",
                  border: "none",
                  bgcolor: "#fafbfc",
                  position: { md: "sticky" },
                  top: { md: 16 },
                }}
              >
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.35,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    bgcolor: "#fff",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 13.5, color: INK }}
                  >
                    Workflows
                  </Typography>
                  <button
                    type="button"
                    className="small-contained-button"
                    onClick={handleAddWorkflow}
                    disabled={saving}
                  >
                    Add
                  </button>
                </Box>
                <Box
                  sx={{
                    maxHeight: { xs: 260, md: "calc(100vh - 220px)" },
                    overflowY: "auto",
                  }}
                >
                  {workflows.map((item) => (
                    <WorkflowListItem
                      key={item.id}
                      item={item}
                      active={!selectedIsNew && selectedWorkflowId === item.id}
                      onSelect={() => setSelectedWorkflow(item.id)}
                      onRename={() => openRenameWorkflow(item)}
                      onDelete={() => {
                        setDeleteWorkflow(item);
                        setOpenDelete(true);
                      }}
                    />
                  ))}
                  {/* The workflow being added sits at the bottom. */}
                  {selectedIsNew ? (
                    <Box
                      sx={{
                        px: 1.75,
                        py: 1.25,
                        bgcolor: PRIMARY_SOFT,
                        borderLeft: `3px solid ${PRIMARY}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          lineHeight: 1.35,
                          color: PRIMARY,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {newWorkflowName || "New workflow"}
                      </Typography>
                    </Box>
                  ) : null}
                  {workflows.length === 0 && !selectedIsNew ? (
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: MUTED,
                        p: 2.5,
                        textAlign: "center",
                      }}
                    >
                      No workflows yet.
                    </Typography>
                  ) : null}
                </Box>
              </Panel>

              {/* Flow canvas */}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ maxWidth: 720, mx: "auto" }}>
                  {showEditor ? (
                    <WorkflowBuilderPanel
                      key={
                        selectedIsNew
                          ? `new:${newDraftSeq}`
                          : `${selectedWorkflowId}:${resetSeq}`
                      }
                      automation={form}
                      tracker={currentTracker}
                      workflowId={selectedWorkflowId}
                      isNew={selectedIsNew}
                      initialName={newWorkflowName}
                      onCancel={
                        selectedIsNew
                          ? () => {
                              setSelectedWorkflow(workflows[0]?.id || null);
                              setNewWorkflowName("");
                            }
                          : () => setResetSeq((seq) => seq + 1)
                      }
                      onSaved={(saved, workflow) => {
                        // Leave ?workflow=new before clearing the name, or the
                        // "ask for a name" effect would reopen the popup.
                        setForm(saved);
                        if (workflow?.id) {
                          setSelectedWorkflow(workflow.id);
                        }
                        setNewWorkflowName("");
                      }}
                    />
                  ) : workflows.length ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 6 }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Panel sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                      {/* One way to add: the Add button on the Workflows list. */}
                      <Typography sx={{ fontSize: 14, color: MUTED }}>
                        Select a workflow, or use Add to create one.
                      </Typography>
                    </Panel>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </div>
    </>
  );
}
